/**
 * Calendar parts from an API date or datetime.
 * `new Date("2026-09-21")` and `new Date("2026-09-21T00:00:00Z")` are UTC
 * instants, so US timezones show the previous calendar day. Due dates and
 * other stored calendar values should keep the YYYY-MM-DD prefix instead.
 */
function calendarDateParts(
  value: string
): { year: number; month: number; day: number } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return null;
  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
  };
}

/**
 * Format a calendar date for display without a timezone day shift.
 * Accepts `YYYY-MM-DD` and ISO datetimes such as `2026-09-21T00:00:00Z`.
 */
export function formatDateOnly(
  value: string | null | undefined,
  fallback = '-',
  options?: Intl.DateTimeFormatOptions,
  locale?: string
): string {
  if (!value) return fallback;

  const parts = calendarDateParts(value);
  if (parts) {
    return new Date(parts.year, parts.month - 1, parts.day).toLocaleDateString(locale, options);
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return fallback;
  return parsed.toLocaleDateString(locale, options);
}

/**
 * Format a stored timestamp using its own calendar day and clock time.
 * Tracking stores the UTC date of a completion; converting the same instant
 * with `toLocaleString()` can show the previous evening in US timezones.
 */
export function formatStoredDateTime(
  value: string | Date | null | undefined,
  fallback = '-'
): string {
  if (!value) return fallback;

  const raw = value instanceof Date ? value.toISOString() : value;
  const match = /^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2}):(\d{2})/.exec(raw);
  if (!match) return formatDateOnly(raw, fallback);

  const date = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    Number(match[4]),
    Number(match[5]),
    Number(match[6])
  );
  return date.toLocaleString();
}

/**
 * Whole calendar days from the viewer's local today until the stored date.
 * Negative when the stored calendar day is in the past.
 */
export function calendarDaysUntil(value: string | null | undefined): number | null {
  if (!value) return null;
  const parts = calendarDateParts(value);
  if (!parts) return null;

  const due = new Date(parts.year, parts.month - 1, parts.day);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}
